import React, { useState } from "react";
import { GraduationCap, LogIn, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { usePartnerAuth } from "../lib/partnerAuth";
import { useNavigate } from "react-router-dom";

export default function PartnerLogin() {
  const { login } = usePartnerAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/partner");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 text-white"
           style={{ background: "linear-gradient(160deg, #0B192C 0%, #0F2C59 60%, #1D4ED8 130%)" }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center"><GraduationCap className="w-6 h-6" /></div>
          <span className="heading-font text-2xl font-bold">Admissio<span className="text-orange-400">.</span> Partner Portal</span>
        </div>
        <div className="space-y-6">
          <h1 className="heading-font text-4xl xl:text-5xl font-bold leading-tight">Track every student <br/> you referred.</h1>
          <p className="text-slate-300 max-w-md">Live status on every application you sent our way — from documents to offer to enrollment. Documents you can download when they land.</p>
        </div>
        <div className="text-xs text-slate-400">© 2026 Blue Kite Visa — Admissio</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-5">
          <div className="mb-4 lg:hidden flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center text-white"><GraduationCap className="w-6 h-6" /></div>
            <span className="heading-font text-2xl font-bold text-slate-900">Partner Portal</span>
          </div>
          <div>
            <h2 className="heading-font text-3xl font-bold text-slate-900">Partner sign-in</h2>
            <p className="text-slate-500 mt-2 text-sm">Use the email and password shared by your BDM. Contact them if you need access.</p>
          </div>
          {err && (
            <div data-testid="partner-login-error" className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{typeof err === "string" ? err : "Login failed"}</span>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs text-slate-600">Email</Label>
            <Input data-testid="partner-email-input" type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-600">Password</Label>
            <Input data-testid="partner-password-input" type="password" required value={password} onChange={e=>setPassword(e.target.value)} />
          </div>
          <Button data-testid="partner-signin-btn" disabled={busy} type="submit" size="lg" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-3">
            <LogIn className="w-5 h-5" />{busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-xs text-slate-400 text-center pt-2">Sessions expire after 7 days. Employee? <a href="/login" className="text-slate-600 underline">Staff login →</a></div>
        </form>
      </div>
    </div>
  );
}
